import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { useGetApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { ROUTES } from "@/app/routes";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type { ChannelConnectionRecord } from "../../../../../packages/shared-types/src/index.js";

export function ChannelConnectionsPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const pluginsHref = ROUTES.tenantPlugins(tenantId!);
  const { data: connections, isLoading } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  useDocumentTitle("Channels");

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(pluginsHref)}>
              Open plugins
            </Button>
            <Button size="sm" disabled>
              <Plus className="h-4 w-4 mr-1" /> New Connection
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  if (!connections || connections.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(pluginsHref)}>
              Open plugins
            </Button>
            <Button size="sm" onClick={() => navigate("/workspace/channels/new")}>
              <Plus className="h-4 w-4 mr-1" /> New Connection
            </Button>
          </div>
        </div>
        <EmptyState title="No channel connections" description="Add a channel connection to connect agents to messaging platforms." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>Channel Connections</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(pluginsHref)}>
            Open plugins
          </Button>
          <Button size="sm" onClick={() => navigate("/workspace/channels/new")}>
            <Plus className="h-4 w-4 mr-1" /> New Connection
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {connections.map((conn) => (
          <Card key={conn.id} className="cursor-pointer hover:border-[var(--border-hover)]" onClick={() => navigate(`/workspace/channels/${conn.id}`)}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{conn.displayName}</CardTitle>
                <StatusBadge status={conn.status} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span style={{ color: "var(--muted-foreground)" }}>Type</span>
                  <p style={{ color: "var(--text-strong)" }}>{conn.channelType}</p>
                </div>
                <div>
                  <span style={{ color: "var(--muted-foreground)" }}>Link</span>
                  <p style={{ color: "var(--text-strong)" }}>{formatLinkStatus(conn.linkStatus)}</p>
                </div>
                <div>
                  <span style={{ color: "var(--muted-foreground)" }}>Credentials</span>
                  <p className="font-mono" style={{ color: "var(--text-strong)" }}>
                    {formatCredentialsRef(conn.credentialsRef)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function formatCredentialsRef(value: unknown): string {
  if (value == null) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object") {
    const provider = (value as { provider?: unknown }).provider;
    const path = (value as { path?: unknown }).path;
    const version = (value as { version?: unknown }).version;

    const providerText = typeof provider === "string" ? provider : "";
    const pathText = typeof path === "string" ? path : "";
    const versionText = typeof version === "string" ? version : "";

    const base = providerText && pathText ? `${providerText}:${pathText}` : "";
    if (!base) {
      return "[invalid credentials ref]";
    }
    return versionText ? `${base}@${versionText}` : base;
  }
  return "—";
}

function formatLinkStatus(value: unknown): string {
  if (typeof value !== "string") {
    return "—";
  }
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
